import {
  useGenerateIncidentReport,
  useSendIncidentReportEmail,
} from '@hakimo-ui/hakimo/data-access';
import { DataVersion, IncidentProduct } from '@hakimo-ui/hakimo/types';
import { toast, useFileDownload } from '@hakimo-ui/hakimo/util';
import { useCallback, useState } from 'react';

/**
 * The generate / preview / email flow for one incident's report.
 *
 * The generated PDF is kept as a File so it can be previewed and
 * attached without the operator ever touching their filesystem;
 * `downloadFile` is wired only to the explicit Download button.
 */
export function useIncidentReportActions(
  product: IncidentProduct,
  onSaveError: (err: Error) => void,
  onSent: () => void,
  dataVersion?: DataVersion
) {
  const { downloadFile } = useFileDownload();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [resetCompose, setResetCompose] = useState(true);

  const { mutate: downloadReport, isLoading: downloading } =
    useGenerateIncidentReport(
      product,
      (file) => downloadFile({ blob: file, fileName: file.name }),
      onSaveError,
      dataVersion
    );

  const { mutateAsync: buildReportAsync, isLoading: preparing } =
    useGenerateIncidentReport(product, undefined, onSaveError, dataVersion);

  const { mutate: sendReport, isLoading: sending } = useSendIncidentReportEmail(
    product,
    () => {
      setShowEmailModal(false);
      onSent();
    },
    onSaveError,
    dataVersion
  );

  const clearGeneratedReport = useCallback(() => {
    setReportFile(null);
    setPreviewFile(null);
  }, []);

  const openPreview = async (alarm: number | string) => {
    setResetCompose(true);
    if (reportFile) {
      setPreviewFile(reportFile);
      setShowPreview(true);
      return;
    }
    try {
      const file = await buildReportAsync({ alarm_id: alarm });
      setReportFile(file);
      setPreviewFile(file);
      setShowPreview(true);
    } catch {
      toast('Could not generate the report. Attach one manually.', {
        type: 'warning',
      });
      setShowEmailModal(true);
    }
  };

  const previewAttachment = (file: File) => {
    setShowEmailModal(false);
    setResetCompose(false);
    setPreviewFile(file);
    setShowPreview(true);
  };

  const composeEmail = () => {
    setShowPreview(false);
    setShowEmailModal(true);
  };

  return {
    downloadReport,
    downloading,
    preparing,
    sendReport,
    sending,
    reportFile,
    previewFile,
    showPreview,
    setShowPreview,
    showEmailModal,
    setShowEmailModal,
    resetCompose,
    openPreview,
    previewAttachment,
    composeEmail,
    clearGeneratedReport,
  };
}
